import { useTranslation } from "react-i18next";
import { Button, cn, Icon, Input, Label, Textarea } from "@nexus/ui";
import {
  checkAttributesJson,
  checkBodyJson,
  formatScheduledJobJsonText,
  type AttributesJsonIssue,
  type BodyJsonIssue,
} from "../lib/scheduled-job-preview";

export interface ScheduledJobAdvancedFormState {
  topicName: string;
  attributesJson: string;
  bodyJson: string;
  retryCount: string;
  maxRetryDurationSeconds: string;
  minBackoffDurationSeconds: string;
  maxBackoffDurationSeconds: string;
  maxDoublings: string;
  /** Whole seconds; blank keeps current / default. */
  attemptDeadlineSeconds: string;
}

interface ScheduledJobAdvancedFieldsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: ScheduledJobAdvancedFormState;
  onChange: (next: ScheduledJobAdvancedFormState) => void;
  disabled?: boolean;
  /** When false, hide the Pub/Sub trio (e.g. non-Pub/Sub jobs — retry-only). */
  showPubsubFields?: boolean;
  idPrefix: string;
}

export function emptyAdvancedFormState(): ScheduledJobAdvancedFormState {
  return {
    topicName: "",
    attributesJson: "{\n  \"eventType\": \"\"\n}",
    bodyJson: "{\n  \n}",
    retryCount: "",
    maxRetryDurationSeconds: "",
    minBackoffDurationSeconds: "",
    maxBackoffDurationSeconds: "",
    maxDoublings: "",
    attemptDeadlineSeconds: "",
  };
}

export function advancedFormFromJob(options: {
  topicName?: string | null;
  attributes?: Record<string, string> | null;
  bodyJson?: unknown | null;
  retryConfig?: {
    retryCount?: number;
    maxRetryDurationSeconds?: number;
    minBackoffDurationSeconds?: number;
    maxBackoffDurationSeconds?: number;
    maxDoublings?: number;
  } | null;
  attemptDeadlineSeconds?: number | null;
}): ScheduledJobAdvancedFormState {
  const attributes = options.attributes ?? {};
  return {
    topicName: options.topicName ?? "",
    attributesJson: `${JSON.stringify(attributes, null, 2)}\n`,
    bodyJson:
      options.bodyJson === undefined || options.bodyJson === null
        ? "{\n  \n}"
        : `${JSON.stringify(options.bodyJson, null, 2)}\n`,
    retryCount: optionalNumberToString(options.retryConfig?.retryCount),
    maxRetryDurationSeconds: optionalNumberToString(options.retryConfig?.maxRetryDurationSeconds),
    minBackoffDurationSeconds: optionalNumberToString(options.retryConfig?.minBackoffDurationSeconds),
    maxBackoffDurationSeconds: optionalNumberToString(options.retryConfig?.maxBackoffDurationSeconds),
    maxDoublings: optionalNumberToString(options.retryConfig?.maxDoublings),
    attemptDeadlineSeconds: optionalNumberToString(
      options.attemptDeadlineSeconds === null
        ? undefined
        : options.attemptDeadlineSeconds,
    ),
  };
}

function optionalNumberToString(value: number | undefined): string {
  return value === undefined ? "" : String(value);
}

/**
 * Collapsible Advanced fields (R4 / R5 / KTD10). Topic, attributes, raw JSON
 * body, retry policy, and attempt deadline (Pub/Sub Job parity).
 */
export function ScheduledJobAdvancedFields({
  open,
  onOpenChange,
  value,
  onChange,
  disabled = false,
  showPubsubFields = true,
  idPrefix,
}: ScheduledJobAdvancedFieldsProps) {
  const { t } = useTranslation();

  function patch(partial: Partial<ScheduledJobAdvancedFormState>) {
    onChange({ ...value, ...partial });
  }

  return (
    <div className="rounded-lg border border-border" data-testid="scheduled-job-advanced">
      <button
        type="button"
        data-testid="scheduled-job-advanced-toggle"
        onClick={() => onOpenChange(!open)}
        disabled={disabled}
        className={cn(
          "flex w-full items-center gap-3 px-4 py-3 text-left transition-colors rounded-lg",
          "hover:bg-accent/50 focus-visible:outline-none focus-visible:bg-accent/50",
          open && "rounded-b-none border-b border-border",
          disabled && "opacity-60",
        )}
        aria-expanded={open}
      >
        <Icon name="settings" size="sm" color="muted" className="shrink-0 self-center" />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <p className="text-sm font-semibold leading-tight text-foreground">
            {t("admin.scheduledJobs.advanced.label", "Advanced")}
          </p>
          <p className="text-xs leading-snug text-muted-foreground">
            {t(
              "admin.scheduledJobs.advanced.hint",
              "Topic, attributes, raw JSON body, retry policy, and attempt deadline.",
            )}
          </p>
        </div>
        <Icon
          name="chevron-down"
          size="xs"
          color="muted"
          className={cn("shrink-0 transition-transform duration-200", open && "rotate-180")}
        />
      </button>
      <div
        className={cn(
          "grid transition-all duration-200 ease-out",
          open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
        )}
      >
        <div className="overflow-hidden">
          <div className="flex flex-col gap-4 p-4">
            {showPubsubFields ? (
              <>
                <div className="flex flex-col gap-2">
                  <Label htmlFor={`${idPrefix}-topic`}>
                    {t("admin.scheduledJobs.advanced.topic", "Topic name")}
                  </Label>
                  <Input
                    id={`${idPrefix}-topic`}
                    value={value.topicName}
                    disabled={disabled}
                    spellCheck={false}
                    onChange={(event) => patch({ topicName: event.target.value })}
                    className="font-mono text-[11px]"
                    placeholder={t(
                      "admin.scheduledJobs.advanced.topicPlaceholder",
                      "projects/…/topics/…",
                    )}
                  />
                </div>
                <JsonObjectField
                  id={`${idPrefix}-attributes`}
                  label={t("admin.scheduledJobs.advanced.attributes", "Attributes (JSON object)")}
                  value={value.attributesJson}
                  rows={5}
                  disabled={disabled}
                  onChange={(attributesJson) => patch({ attributesJson })}
                  statusId={`${idPrefix}-attributes-status`}
                />
                <JsonBodyField
                  id={`${idPrefix}-body`}
                  label={t("admin.scheduledJobs.advanced.body", "Body (raw JSON)")}
                  value={value.bodyJson}
                  rows={6}
                  disabled={disabled}
                  onChange={(bodyJson) => patch({ bodyJson })}
                  statusId={`${idPrefix}-body-status`}
                />
              </>
            ) : null}

            <div className="flex flex-col gap-2">
              <span className="text-xs font-medium text-foreground">
                {t("admin.scheduledJobs.advanced.retryTitle", "Retry policy")}
              </span>
              <p className="text-[11px] text-muted-foreground">
                {t(
                  "admin.scheduledJobs.advanced.retryHint",
                  "Leave blank to keep Cloud Scheduler defaults. Durations are whole seconds.",
                )}
              </p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <RetryNumberField
                  id={`${idPrefix}-retry-count`}
                  label={t("admin.scheduledJobs.advanced.retryCount", "Retry count")}
                  value={value.retryCount}
                  disabled={disabled}
                  onChange={(retryCount) => patch({ retryCount })}
                />
                <RetryNumberField
                  id={`${idPrefix}-max-doublings`}
                  label={t("admin.scheduledJobs.advanced.maxDoublings", "Max doublings")}
                  value={value.maxDoublings}
                  disabled={disabled}
                  onChange={(maxDoublings) => patch({ maxDoublings })}
                />
                <RetryNumberField
                  id={`${idPrefix}-min-backoff`}
                  label={t(
                    "admin.scheduledJobs.advanced.minBackoff",
                    "Min backoff (seconds)",
                  )}
                  value={value.minBackoffDurationSeconds}
                  disabled={disabled}
                  onChange={(minBackoffDurationSeconds) => patch({ minBackoffDurationSeconds })}
                />
                <RetryNumberField
                  id={`${idPrefix}-max-backoff`}
                  label={t(
                    "admin.scheduledJobs.advanced.maxBackoff",
                    "Max backoff (seconds)",
                  )}
                  value={value.maxBackoffDurationSeconds}
                  disabled={disabled}
                  onChange={(maxBackoffDurationSeconds) => patch({ maxBackoffDurationSeconds })}
                />
                <RetryNumberField
                  id={`${idPrefix}-max-retry-duration`}
                  label={t(
                    "admin.scheduledJobs.advanced.maxRetryDuration",
                    "Max retry duration (seconds)",
                  )}
                  value={value.maxRetryDurationSeconds}
                  disabled={disabled}
                  onChange={(maxRetryDurationSeconds) => patch({ maxRetryDurationSeconds })}
                />
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <span className="text-xs font-medium text-foreground">
                {t("admin.scheduledJobs.advanced.attemptDeadlineTitle", "Attempt deadline")}
              </span>
              <p className="text-[11px] text-muted-foreground">
                {t(
                  "admin.scheduledJobs.advanced.attemptDeadlineHint",
                  "Whole seconds (15–1800). Cloud Scheduler ignores this for Pub/Sub delivery; stored on the job for Console parity.",
                )}
              </p>
              <RetryNumberField
                id={`${idPrefix}-attempt-deadline`}
                label={t(
                  "admin.scheduledJobs.advanced.attemptDeadline",
                  "Attempt deadline (seconds)",
                )}
                value={value.attemptDeadlineSeconds}
                disabled={disabled}
                onChange={(attemptDeadlineSeconds) => patch({ attemptDeadlineSeconds })}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RetryNumberField({
  id,
  label,
  value,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="number"
        inputMode="numeric"
        min={0}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        className="font-mono text-[11px]"
      />
    </div>
  );
}

/**
 * Quiet Format control — same affordance as Events publish; stays secondary to
 * the JSON itself.
 */
function JsonFormatAction({
  isDisabled,
  onClick,
}: {
  isDisabled: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      disabled={isDisabled}
      onClick={onClick}
      className="h-6 rounded-sm px-2 text-[11px] font-normal text-muted-foreground hover:text-foreground"
    >
      {t("admin.scheduledJobs.advanced.format", "Format")}
    </Button>
  );
}

function JsonObjectField({
  id,
  label,
  value,
  rows,
  disabled,
  onChange,
  statusId,
}: {
  id: string;
  label: string;
  value: string;
  rows: number;
  disabled?: boolean;
  onChange: (value: string) => void;
  statusId: string;
}) {
  const { t } = useTranslation();
  const check = checkAttributesJson(value);
  const formatted = formatScheduledJobJsonText(value);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <Label htmlFor={id}>{label}</Label>
        <JsonFormatAction
          isDisabled={disabled === true || formatted === null || formatted === value}
          onClick={() => {
            if (formatted !== null) onChange(formatted);
          }}
        />
      </div>
      <Textarea
        id={id}
        value={value}
        rows={rows}
        disabled={disabled}
        spellCheck={false}
        aria-invalid={!check.ok}
        aria-describedby={statusId}
        onChange={(event) => onChange(event.target.value)}
        className="resize-y font-mono text-[11px] leading-[1.6]"
      />
      <p
        id={statusId}
        aria-live="polite"
        className={cn(
          "text-[11px]",
          !check.ok
            ? "text-error"
            : check.missingEventType
              ? "text-warning"
              : "text-muted-foreground",
        )}
      >
        {!check.ok
          ? attributesIssueText(check.issue, t)
          : check.missingEventType
            ? t(
                "admin.scheduledJobs.advanced.attributesMissingEventType",
                "Valid JSON — add a non-empty eventType string.",
              )
            : t("admin.scheduledJobs.advanced.attributesValid", "Valid JSON — {{count}} attributes", {
                count: Object.keys(check.value).length,
              })}
      </p>
    </div>
  );
}

function JsonBodyField({
  id,
  label,
  value,
  rows,
  disabled,
  onChange,
  statusId,
}: {
  id: string;
  label: string;
  value: string;
  rows: number;
  disabled?: boolean;
  onChange: (value: string) => void;
  statusId: string;
}) {
  const { t } = useTranslation();
  const check = checkBodyJson(value);
  const formatted = formatScheduledJobJsonText(value);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <Label htmlFor={id}>{label}</Label>
        <JsonFormatAction
          isDisabled={disabled === true || formatted === null || formatted === value}
          onClick={() => {
            if (formatted !== null) onChange(formatted);
          }}
        />
      </div>
      <Textarea
        id={id}
        value={value}
        rows={rows}
        disabled={disabled}
        spellCheck={false}
        aria-invalid={!check.ok}
        aria-describedby={statusId}
        onChange={(event) => onChange(event.target.value)}
        className="resize-y font-mono text-[11px] leading-[1.6]"
        placeholder="{}"
      />
      <p
        id={statusId}
        aria-live="polite"
        className={cn("text-[11px]", check.ok ? "text-muted-foreground" : "text-error")}
      >
        {check.ok
          ? t("admin.scheduledJobs.advanced.bodyValid", "Valid JSON.")
          : bodyIssueText(check.issue, t)}
      </p>
    </div>
  );
}

function attributesIssueText(
  issue: AttributesJsonIssue,
  t: (key: string, fallback: string, options?: Record<string, unknown>) => string,
): string {
  switch (issue.kind) {
    case "invalid-json":
      return issue.at
        ? t(
            "admin.scheduledJobs.advanced.error.jsonAt",
            "Invalid JSON at line {{line}}, column {{column}}.",
            { line: issue.at.line, column: issue.at.column },
          )
        : t("admin.scheduledJobs.advanced.error.json", "This is not valid JSON.");
    case "not-an-object":
      return t(
        "admin.scheduledJobs.advanced.error.attributesObject",
        "Attributes must be a JSON object.",
      );
    case "non-string-values":
      return t(
        "admin.scheduledJobs.advanced.error.attributesStrings",
        "Attribute values must be strings ({{keys}}).",
        { keys: issue.keys.join(", ") },
      );
  }
}

function bodyIssueText(
  issue: BodyJsonIssue,
  t: (key: string, fallback: string, options?: Record<string, unknown>) => string,
): string {
  switch (issue.kind) {
    case "empty":
      return t("admin.scheduledJobs.advanced.error.bodyEmpty", "Body JSON is required.");
    case "invalid-json":
      return issue.at
        ? t(
            "admin.scheduledJobs.advanced.error.jsonAt",
            "Invalid JSON at line {{line}}, column {{column}}.",
            { line: issue.at.line, column: issue.at.column },
          )
        : t("admin.scheduledJobs.advanced.error.json", "This is not valid JSON.");
  }
}
